/* eslint-disable @typescript-eslint/no-unused-vars */
import { Edit, Check } from '@mui/icons-material';
import { Stack, Typography, TextField, IconButton, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { setSenderProfileId, setSendName } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { channelIcons } from 'domains/campaigns/pages/CurationsPage/utils';
import { DirectMailSendInputState } from 'domains/campaigns/types';
import { CampaignStatus } from 'models/enums';
import { DirectMailSenderProfile } from 'domains/content/types';
import FormLayout from 'domains/core/components/FormLayout';
import InputLabel from 'domains/core/components/InputLabel';
import Select from 'domains/core/components/Select';
import useSenderProfiles from 'hooks/queries/useSenderProfiles';
import useQueryParams from 'hooks/useQueryParams';
import theme from 'theme';
import { formatAddressLabel } from './utils';
import SelectOrUploadWrapper from './SelectOrUploadWrapper';

const SAMPLE_PDF_URL = '/assets/sample.pdf';
const GUIDING_TEXT = (
    <>
        Focus on the goal of your customer interaction and select or upload content that best aligns with your
        objective. To ensure an uploaded file meets the required format, you can download a sample file{' '}
        <a
            href={SAMPLE_PDF_URL}
            download="sample.pdf"
            style={{ color: theme.palette.secondary.main, textDecoration: 'underline' }}
        >
            here
        </a>{' '}
        for reference.
    </>
);

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    dispatchCampaignAction: React.Dispatch<any>;
    setContentPreviewId: (id: number) => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const DirectMailContentStep = ({
    activeSendIndex,
    campaignState,
    contentPreviewId,
    dispatchCampaignAction,
    setContentPreviewId,
    setIsContinueEnabled,
    setNextStep,
    onContentUnavailableChange,
}: Props) => {
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    // Skip send name updates when editing existing draft/paused curations
    const isEditingExistingDraftOrPaused = status === CampaignStatus.DRAFT || status === CampaignStatus.PAUSED;

    // Edit sendname feature is disabled for non-A/B testing flows
    const editSendnameEnabled = false;
    const currentSend = campaignState.sends[activeSendIndex];
    const { content, senderProfileId, name: sendName } = currentSend as DirectMailSendInputState;

    const { data: senderProfiles } = useSenderProfiles({ type: 'direct-mail' });

    // State for editable send name
    const [isEditingName, setIsEditingName] = useState(false);
    const curationName = campaignState.name || '';
    const contentName = content?.name || '';

    // Generate the default name with format: {curationName} - {contentName}.{uuid}
    const generateDefaultName = (cntName?: string) => {
        const effectiveContentName = cntName ?? contentName;
        const uuid = uuidv4();
        const curationPart = curationName || '';
        const contentPart = effectiveContentName || '';

        return `${curationPart} - ${contentPart}.${uuid}`;
    };

    // Get the display name: use sendName from campaignState if available, otherwise use default
    const getDisplayName = () => {
        if (sendName && sendName.trim() !== '') {
            return sendName;
        }
        return generateDefaultName();
    };
    const [customName, setCustomName] = useState<string>(getDisplayName());

    // Validation: Check if name is duplicate
    const isDuplicateName = (() => {
        if (customName.trim() === '') return false;
        // Check if any other send has the same name
        return campaignState.sends.some((send, index) => index !== activeSendIndex && send.name === customName);
    })();
    const hasNameError = isDuplicateName;
    const nameErrorMessage = isDuplicateName ? 'All send names must be different' : '';

    // Sync local state with campaignState when sendName changes
    useEffect(() => {
        setCustomName(getDisplayName());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendName, activeSendIndex]);

    // Initialize send name in campaignState if it's empty
    // Skip for existing draft/paused curations to preserve existing names
    useEffect(() => {
        if (isEditingExistingDraftOrPaused) return;

        if (!sendName || sendName.trim() === '') {
            const defaultName = generateDefaultName();
            dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex, curationName, contentName, isEditingExistingDraftOrPaused]);

    // Reset send name when content changes
    // Skip for existing draft/paused curations to preserve existing names
    useEffect(() => {
        if (isEditingExistingDraftOrPaused) return;

        if (contentName) {
            const newDefaultName = generateDefaultName(contentName);
            dispatchCampaignAction(setSendName(newDefaultName, activeSendIndex));
            setCustomName(newDefaultName);
        } else if (!content?.id) {
            // Content was cleared - format: {curationName} - .{uuid}
            const defaultName = `${curationName} - .${uuidv4()}`;
            dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
            setCustomName(defaultName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content?.id, isEditingExistingDraftOrPaused]);

    useEffect(() => {
        // Direct mail curations don't support delayed sends yet, but we're leaving this here for when it does
        if (activeSendIndex > 0) {
            setNextStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
        } else {
            setNextStep(CustomCampaignSteps.SCHEDULE_CURATION);
        }
        // The dependency array is empty because this useEffect should run exactly once after the initial rendering of the component.
        // The hook is not dependent on any props or state variables to re-run. This is intended to perform the initial setup based on
        // the `activeSendIndex` value when the component mounts without reacting to any further updates or re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Disable continue if there's a name error
        if (hasNameError) {
            setIsContinueEnabled(false);
            return;
        }
        const isEnabled = !!content.id && !!senderProfileId;
        setIsContinueEnabled(isEnabled);
    }, [content.id, senderProfileId, setIsContinueEnabled, hasNameError]);

    // Handlers for editing send name
    const handleEditClick = () => {
        setIsEditingName(true);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomName(event.target.value);
    };

    const handleNameSave = () => {
        // Don't save if name is duplicate
        if (hasNameError) {
            return;
        }

        setIsEditingName(false);
        // Dispatch action to save customName to campaignState
        dispatchCampaignAction(setSendName(customName, activeSendIndex));
    };

    const handleNameKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleNameSave();
        }
    };

    const senderProfilesValues =
        senderProfiles?.map((profile: DirectMailSenderProfile) => ({
            value: profile.id,
            label: formatAddressLabel(profile),
        })) || [];

    const onSenderProfileChange = (e: any) =>
        dispatchCampaignAction(setSenderProfileId(e.target.value, activeSendIndex));

    const StepIcon = channelIcons.DIRECTMAIL;

    return (
        <FormLayout header="select content" icon={<StepIcon />} guidingText={GUIDING_TEXT} stepName="direct mail">
            <Stack rowGap={4}>
                {/* Editable send name section - only shown when feature flag is enabled */}
                {editSendnameEnabled && (
                    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {isEditingName ? (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <TextField
                                            value={customName}
                                            onChange={handleNameChange}
                                            onKeyPress={handleNameKeyPress}
                                            onBlur={handleNameSave}
                                            autoFocus
                                            multiline
                                            variant="standard"
                                            error={hasNameError}
                                            helperText={nameErrorMessage}
                                            sx={{
                                                width: '100%',
                                                '& .MuiInputBase-input': {
                                                    fontSize: '1.25rem',
                                                    fontWeight: 'bold',
                                                    wordBreak: 'break-word',
                                                },
                                            }}
                                        />
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={handleNameSave}
                                        disabled={hasNameError}
                                        sx={{ padding: 0.5, mt: 0.5, flexShrink: 0 }}
                                    >
                                        <Check fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {customName}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={handleEditClick}
                                            sx={{ padding: 0.5, flexShrink: 0 }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    {hasNameError && (
                                        <Typography variant="caption" color="error" fontSize={12}>
                                            {nameErrorMessage}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
                <SelectOrUploadWrapper
                    activeSendIndex={activeSendIndex}
                    contentPreviewId={contentPreviewId}
                    currentSend={currentSend as DirectMailSendInputState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setContentPreviewId={setContentPreviewId}
                    onContentUnavailableChange={onContentUnavailableChange}
                />
                <Select
                    fullWidth
                    label={<InputLabel label="Sender profile" labelIconTooltipText="Who is sending this?" />}
                    menuItemStyles={{ textTransform: 'none' }}
                    onChange={onSenderProfileChange}
                    required
                    value={senderProfileId ?? ''}
                    values={senderProfilesValues}
                />
            </Stack>
        </FormLayout>
    );
};

export default DirectMailContentStep;
